import React, { useContext, useMemo } from "react";
import ColumnSetting from "@ant-design/pro-table/es/components/ColumnSetting";
import { TableContext } from "@ant-design/pro-table/es/Store/Provide";
import { genColumnKey } from "@ant-design/pro-table/es/utils/index";

/**
 * ProTable Provide 里 defaultColumnKeyMap 的列 key 规则（顶层 forEach 的 index）。
 */
function topLevelColumnKey(column, index) {
  return genColumnKey(
    column.key != null && column.key !== undefined
      ? column.key
      : column.dataIndex,
    index
  );
}

/**
 * 与 genProColumnToColumn 一致：嵌套列用父列的 columnKey + 子下标。
 */
function nestedColumnKey(columnProps, columnsIndex, parentColumnKey) {
  const key = columnProps.key;
  const dataIndex = columnProps.dataIndex;
  const keyForGen =
    key || (dataIndex == null ? undefined : dataIndex.toString());
  return genColumnKey(keyForGen, [
    parentColumnKey,
    columnsIndex,
  ].filter(Boolean).join("-"));
}

function fixedFromColumnsState(col, columnKey, columnsMap) {
  const state = columnsMap[columnKey];
  if (state != null && Object.prototype.hasOwnProperty.call(state, "fixed")) {
    return state.fixed;
  }
  return col.fixed;
}

function mergeNestedColumns(children, columnsMap, parentColumnKey) {
  return (children || []).map((col, index) => {
    const columnKey = nestedColumnKey(col, index, parentColumnKey);
    const fixed = fixedFromColumnsState(col, columnKey, columnsMap);
    const next = { ...col, fixed };
    if (col.children && col.children.length) {
      next.children = mergeNestedColumns(
        col.children,
        columnsMap,
        columnKey
      );
    }
    return next;
  });
}

/**
 * 将 columnsMap 里的 fixed 合并进列定义，再交给 ColumnSetting。
 * 否则 GroupCheckboxList 只读 item.fixed，用户点击图钉只改了 columnsMap，
 * 「固定在左侧/右侧」分组与图钉状态会不一致。
 */
export function mergeColumnFixedFromColumnsMap(columns, columnsMap) {
  if (!Array.isArray(columns) || !columnsMap) {
    return columns;
  }
  return columns.map((col, index) => {
    const columnKey = topLevelColumnKey(col, index);
    const fixed = fixedFromColumnsState(col, columnKey, columnsMap);
    const next = { ...col, fixed };
    if (col.children && col.children.length) {
      next.children = mergeNestedColumns(
        col.children,
        columnsMap,
        columnKey
      );
    }
    return next;
  });
}

export default function TableColumnSetting(props) {
  const { columns, children, ...rest } = props;
  const ctx = useContext(TableContext);
  const columnsMap = ctx?.columnsMap;
  const hasValidColumnsMap = columnsMap != null && typeof columnsMap === "object";

  const mergedColumns = useMemo(
    () => (hasValidColumnsMap ? mergeColumnFixedFromColumnsMap(columns, columnsMap) : columns),
    [columns, columnsMap, hasValidColumnsMap]
  );

  if (!hasValidColumnsMap) {
    return children ?? null;
  }

  return <ColumnSetting {...rest} columns={mergedColumns} />;
}
